import React, { useState } from "react";
import {
  HStack,
  IconButton,
  Badge,
  Collapse,
  Text,
  Spinner,
  Box,
  Flex,
  Tag,
  Wrap,
  WrapItem,
  VStack,
  Icon,
} from "@chakra-ui/react";
import { FiUsers } from "react-icons/fi";
import { JobRunStatus, ScheduledJobRunProto } from "build-ts/schedule";
import { StatusInfo } from "modules/analyst/containers/AnalysisRunCard";
import AnalysisScenarioInsightsCard from "modules/analyst/containers/AnalysisScenarioInsightsCard";
import AnalysisLogsTerminal from "modules/analyst/components/AnalysisLogsTerminal";
import { AppIcons } from "constants/icons";

interface AnalysisRunCardProps {
  runDetails: ScheduledJobRunProto | null;
  isLoading: boolean;
  isRefreshing: boolean;
  formattedDate: string;
  statusInfo: StatusInfo | null;
  onRefresh: () => void;
  onHeaderClick: () => void;
  hideRunDetails?: boolean;
}

const AnalysisRunCard: React.FC<AnalysisRunCardProps> = ({
  runDetails,
  isLoading,
  isRefreshing,
  formattedDate,
  statusInfo,
  onRefresh,
  onHeaderClick,
  hideRunDetails = false,
}) => {
  const [showLogs, setShowLogs] = useState(false);

  const isInProgress = runDetails?.status === JobRunStatus.IN_PROGRESS || runDetails?.status === JobRunStatus.PENDING;

  const hasLogs = !!(runDetails?.run_logs && runDetails.run_logs.length > 0);

  return (
    <Flex
      p={3}
      align="flex-start"
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.300"
      bg="white"
      transition="all 0.15s ease"
      _hover={{ borderColor: "gray.400" }}
      role="group"
      direction="column"
    >
      {/* Header row: date | status badge | refresh (if in-progress) | chevron toggle */}
      <Flex align="center" w="100%">
        <Box flex="1" minW={0} cursor="pointer" onClick={onHeaderClick}>
          <Text fontSize="md" fontWeight="600" color="fg.default" noOfLines={1} align="left" m={0}>
            {formattedDate}
          </Text>
        </Box>

        <HStack spacing={1} align="center">
          {runDetails?.status && statusInfo && (
            <Badge colorScheme={statusInfo.color} fontSize="xs">
              {statusInfo.label}
            </Badge>
          )}

          {isInProgress && (
            <IconButton
              aria-label="Refresh run status"
              icon={isRefreshing ? <Spinner size="xs" /> : <AppIcons.refresh boxSize={4} />}
              size="xs"
              variant="ghost"
              colorScheme="gray"
              isDisabled={isRefreshing}
              onClick={e => {
                e.stopPropagation();
                onRefresh();
              }}
            />
          )}

          <IconButton
            aria-label={showLogs ? "Collapse logs" : "Expand logs"}
            icon={<Icon as={AppIcons.terminal} boxSize={4} />}
            size="xs"
            variant={showLogs ? "solid" : "ghost"}
            colorScheme={showLogs ? "purple" : "gray"}
            color={showLogs ? "white" : "purple.700"}
            _hover={{ bg: "purple.700", color: "white" }}
            onClick={e => {
              e.stopPropagation();
              setShowLogs(prev => !prev);
            }}
          />
        </HStack>
      </Flex>

      {runDetails && (
        <Box w="100%" mt={1}>
          {/* Run logs — above run details */}
          <Collapse in={showLogs} animateOpacity>
            <Box mb={hasLogs && !hideRunDetails ? 3 : 0}>
              {hasLogs ? (
                <AnalysisLogsTerminal logs={runDetails.run_logs} />
              ) : (
                <Text fontSize="xs" color="gray.400" py={2}>
                  No run logs available.
                </Text>
              )}
            </Box>
          </Collapse>

          {/* Scenario / column-sync details — hidden on the Jobs page */}
          {!hideRunDetails &&
            (isLoading ? (
              <HStack justify="center" py={4}>
                <Spinner size="sm" />
                <Text fontSize="sm">Loading run details...</Text>
              </HStack>
            ) : runDetails?.scenario_run ? (
              <AnalysisScenarioInsightsCard scenarioRun={runDetails.scenario_run} />
            ) : runDetails?.column_sync_run ? (
              <VStack align="stretch" spacing={2} pt={1}>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme="green" variant="subtle" fontSize="xs">
                    {runDetails.column_sync_run.populated_analyst_headers.length} synced
                  </Badge>
                  {runDetails.column_sync_run.skipped_analyst_headers.length > 0 && (
                    <Badge colorScheme="red" variant="subtle" fontSize="xs">
                      {runDetails.column_sync_run.skipped_analyst_headers.length} skipped
                    </Badge>
                  )}
                </HStack>
                {runDetails.column_sync_run.populated_analyst_headers.length > 0 && (
                  <Box>
                    <HStack spacing={1} mb={1}>
                      <Icon as={FiUsers} boxSize={3} color="gray.400" />
                      <Text fontSize="xs" color="gray.500" m={0} fontWeight="600">
                        Synced analysts
                      </Text>
                    </HStack>
                    <Wrap spacing={1}>
                      {runDetails.column_sync_run.populated_analyst_headers.map(h => (
                        <WrapItem key={h.id}>
                          <Tag size="sm" colorScheme="purple" variant="subtle" borderRadius="md">
                            {h.name || h.id}
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}
                {runDetails.column_sync_run.skipped_analyst_headers.length > 0 && (
                  <Box>
                    <HStack spacing={1} mb={1}>
                      <Icon as={FiUsers} boxSize={3} color="red.400" />
                      <Text fontSize="xs" color="red.400" m={0} fontWeight="600">
                        Skipped analysts
                      </Text>
                    </HStack>
                    <Wrap spacing={1}>
                      {runDetails.column_sync_run.skipped_analyst_headers.map(h => (
                        <WrapItem key={h.id}>
                          <Tag size="sm" colorScheme="red" variant="subtle" borderRadius="md">
                            {h.name || h.id}
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  </Box>
                )}
              </VStack>
            ) : null)}
        </Box>
      )}
    </Flex>
  );
};

export default AnalysisRunCard;
